import { useEffect, useState } from "react";
import api, { fmtMXN, fmtDate, EXPENSE_CATEGORIES, EXPENSE_PAYMENT_METHODS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Plus, X, HandCoins, Wallet, ChevronDown, Loader2, Paperclip, ShoppingCart, FileText } from "lucide-react";
import ReceiptViewer from "@/components/ReceiptViewer";
import SignaturePad from "@/components/SignaturePad";
import { toast } from "sonner";

const TYPE_LABEL = { prestamo: "Préstamo", adelanto: "Adelanto", cargo: "Cargo por compra" };
const TYPE_STYLE = {
  prestamo: { background: "rgba(217,70,239,0.12)", color: "var(--magenta)" },
  adelanto: { background: "rgba(34,211,238,0.12)", color: "var(--cyan)" },
  cargo: { background: "rgba(251,191,36,0.12)", color: "#fbbf24" },
};

export default function EmployeeLoansTab({ employees }) {
  const { user } = useAuth();
  const canManage = ["admin", "contador", "auxiliar"].includes(user?.role);
  const [data, setData] = useState({ items: [], summary: null });
  const [loading, setLoading] = useState(true);
  const [openNew, setOpenNew] = useState(false);
  const [abonoLoan, setAbonoLoan] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [viewTicket, setViewTicket] = useState(null);
  const [openStatement, setOpenStatement] = useState(false);
  const [filterStatus, setFilterStatus] = useState("activo");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/employee-loans");
      setData(data);
    } catch {
      toast.error("No se pudieron cargar los préstamos");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este préstamo?")) return;
    try {
      await api.delete(`/employee-loans/${id}`);
      toast.success("Préstamo eliminado");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo eliminar");
    }
  };

  const items = data.items.filter((l) => !filterStatus || l.status === filterStatus);
  const s = data.summary;

  return (
    <div className="space-y-4" data-testid="employee-loans-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3 flex-wrap">
          {s && (
            <>
              <span className="px-3 py-1.5 rounded-sm text-xs font-mono" style={{ background: "rgba(251,191,36,0.1)", color: "#fbbf24", border: "1px solid rgba(251,191,36,0.3)" }} data-testid="loans-total-saldo">
                Saldo vivo: <b>{fmtMXN(s.total_saldo)}</b> · {s.activos} activo{s.activos === 1 ? "" : "s"}
              </span>
              <span className="px-3 py-1.5 rounded-sm text-xs font-mono text-slate-400" style={{ background: "rgba(15,23,42,0.6)", border: "1px solid var(--border)" }}>
                Total histórico: {fmtMXN(s.total_prestado)}
              </span>
            </>
          )}
          {["activo", "saldado", ""].map((st) => (
            <button key={st || "all"} onClick={() => setFilterStatus(st)} data-testid={`loans-filter-${st || "all"}`}
              className="px-2.5 py-1 text-[10px] uppercase tracking-widest rounded-sm"
              style={{
                background: filterStatus === st ? "rgba(34,211,238,0.15)" : "transparent",
                color: filterStatus === st ? "var(--cyan)" : "#94a3b8",
                border: filterStatus === st ? "1px solid var(--cyan)" : "1px solid var(--border)",
              }}>
              {st === "" ? "Todos" : st === "activo" ? "Activos" : "Saldados"}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          {canManage && (
            <button onClick={() => setOpenStatement(true)} data-testid="loan-statement-btn"
              className="btn-ghost px-3 py-2 rounded-sm flex items-center gap-2 text-xs uppercase tracking-widest font-semibold border" style={{ borderColor: "var(--border)" }}>
              <FileText className="w-4 h-4" /> Estado de cuenta
            </button>
          )}
          <button onClick={() => setOpenNew(true)} data-testid="new-employee-loan-btn"
            className="btn-primary px-4 py-2 rounded-sm flex items-center gap-2 text-xs uppercase tracking-widest font-semibold">
            <Plus className="w-4 h-4" /> Préstamo / Cargo
          </button>
        </div>
      </div>

      {loading ? (
        <div className="panel p-8 text-center text-slate-400">Cargando…</div>
      ) : items.length === 0 ? (
        <div className="panel p-10 text-center text-slate-500" data-testid="loans-empty">
          <HandCoins className="w-10 h-10 mx-auto mb-3 text-slate-600" />
          Sin registros {filterStatus === "activo" ? "activos" : filterStatus === "saldado" ? "saldados" : ""}.
        </div>
      ) : (
        <div className="panel overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="panel-header">
                <tr className="text-left">
                  <th className="label-mono px-3 py-2.5">Trabajador</th>
                  <th className="label-mono px-3 py-2.5">Tipo</th>
                  <th className="label-mono px-3 py-2.5">Fecha</th>
                  <th className="label-mono px-3 py-2.5 text-right">Monto</th>
                  <th className="label-mono px-3 py-2.5 text-right">Abonado</th>
                  <th className="label-mono px-3 py-2.5 text-right">Saldo</th>
                  <th className="label-mono px-3 py-2.5 text-center">Estado</th>
                  <th className="label-mono px-3 py-2.5 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {items.map((l) => {
                  const abonado = l.amount - l.balance;
                  const isOpen = expanded === l.id;
                  return [
                    <tr key={l.id} data-testid={`loan-row-${l.id}`} className="row-hover" style={{ borderBottom: "1px solid var(--border)" }}>
                      <td className="px-3 py-2.5">
                        <div className="font-semibold text-slate-100">{l.employee_name}</div>
                        {(l.concept || l.reason) && <div className="text-[11px] text-slate-500 truncate max-w-[200px]">{l.concept || l.reason}</div>}
                      </td>
                      <td className="px-3 py-2.5">
                        <span className="px-2 py-0.5 rounded-sm text-[10px] font-semibold uppercase"
                          style={TYPE_STYLE[l.type] || TYPE_STYLE.prestamo}>
                          {TYPE_LABEL[l.type] || l.type}
                        </span>
                        {l.deduction_mode === "parcialidades" && l.installment_amount > 0 && (
                          <div className="text-[10px] font-mono text-slate-500 mt-1">{fmtMXN(l.installment_amount)} / raya</div>
                        )}
                      </td>
                      <td className="px-3 py-2.5 font-mono text-xs text-slate-400">{fmtDate(l.loan_date)}</td>
                      <td className="px-3 py-2.5 text-right font-mono text-slate-200">{fmtMXN(l.amount)}</td>
                      <td className="px-3 py-2.5 text-right font-mono" style={{ color: "var(--green)" }}>{fmtMXN(abonado)}</td>
                      <td className="px-3 py-2.5 text-right font-mono font-bold" style={{ color: l.balance > 0 ? "#fbbf24" : "var(--green)" }}>{fmtMXN(l.balance)}</td>
                      <td className="px-3 py-2.5 text-center">
                        <span className={`px-2 py-0.5 rounded-sm text-[10px] font-semibold ${l.status === "activo" ? "badge-pending" : "badge-success"}`}>
                          {l.status === "activo" ? "ACTIVO" : "SALDADO"}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-right whitespace-nowrap">
                        {l.receipt_path && (
                          <button onClick={() => setViewTicket(l)} data-testid={`loan-ticket-${l.id}`} title="Ver ticket"
                            className="text-xs font-semibold hover:underline mr-3 text-slate-400">
                            <Paperclip className="w-3.5 h-3.5 inline" /> TICKET
                          </button>
                        )}
                        {(l.payments || []).length > 0 && (
                          <button onClick={() => setExpanded(isOpen ? null : l.id)} data-testid={`loan-history-${l.id}`}
                            className="text-xs font-semibold hover:underline mr-3 text-slate-400">
                            HISTORIAL <ChevronDown className={`w-3 h-3 inline transition-transform ${isOpen ? "rotate-180" : ""}`} />
                          </button>
                        )}
                        {canManage && l.status === "activo" && (
                          <button onClick={() => setAbonoLoan(l)} data-testid={`loan-abono-${l.id}`}
                            className="text-xs font-semibold hover:underline mr-3" style={{ color: "var(--cyan)" }}>ABONAR</button>
                        )}
                        {user?.role === "admin" && (l.payments || []).length === 0 && (
                          <button onClick={() => remove(l.id)} data-testid={`loan-delete-${l.id}`}
                            className="text-xs font-semibold hover:underline" style={{ color: "#fca5a5" }}>ELIMINAR</button>
                        )}
                      </td>
                    </tr>,
                    isOpen && (
                      <tr key={`${l.id}-hist`}>
                        <td colSpan={8} className="px-6 py-3" style={{ background: "rgba(15,23,42,0.5)" }}>
                          <div className="label-mono mb-2 text-[10px]">// ABONOS</div>
                          {(l.payments || []).map((p) => (
                            <div key={p.id} className="flex items-center gap-3 text-xs py-1 flex-wrap">
                              <span className="font-mono text-slate-400">{fmtDate(p.date)}</span>
                              <span className="font-mono font-bold" style={{ color: "var(--green)" }}>+{fmtMXN(p.amount)}</span>
                              <span className="text-slate-400 flex-1">{p.note}{p.payroll_id ? " (nómina)" : ""}</span>
                              <span className="text-slate-500">{p.created_by_name}</span>
                            </div>
                          ))}
                        </td>
                      </tr>
                    ),
                  ];
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {openNew && <NewLoanModal employees={employees} onClose={() => setOpenNew(false)} onDone={() => { setOpenNew(false); load(); }} />}
      {abonoLoan && <AbonoModal loan={abonoLoan} onClose={() => setAbonoLoan(null)} onDone={() => { setAbonoLoan(null); load(); }} />}
      {viewTicket && <ReceiptViewer fileId={viewTicket.receipt_path} title={viewTicket.concept || viewTicket.employee_name} onClose={() => setViewTicket(null)} />}
      {openStatement && <StatementModal loans={data.items} onClose={() => setOpenStatement(false)} />}
    </div>
  );
}

function StatementModal({ loans, onClose }) {
  const emps = Object.values(
    loans.reduce((acc, l) => {
      const e = acc[l.employee_id] || { id: l.employee_id, name: l.employee_name, saldo: 0, count: 0 };
      e.saldo += l.status === "activo" ? (l.balance || 0) : 0;
      e.count += 1;
      acc[l.employee_id] = e;
      return acc;
    }, {})
  ).sort((a, b) => a.name.localeCompare(b.name));
  const [empId, setEmpId] = useState(emps[0]?.id || "");
  const [showSign, setShowSign] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const selected = emps.find((e) => e.id === empId);

  const download = async (signature) => {
    setDownloading(true);
    try {
      const { data } = await api.post(`/employee-loans/statement/${empId}/pdf`, { signature }, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `estado_cuenta_${(selected?.name || "trabajador").replace(/[^a-zA-Z0-9 _-]/g, "").trim().replace(/\s+/g, "_")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Estado de cuenta descargado");
      onClose();
    } catch {
      toast.error("No se pudo generar el estado de cuenta");
    } finally {
      setDownloading(false);
      setShowSign(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(5,8,18,0.85)", backdropFilter: "blur(6px)" }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md panel-strong" data-testid="loan-statement-modal">
        <div className="px-6 py-4 border-b flex items-center justify-between" style={{ borderColor: "var(--border)" }}>
          <div>
            <div className="label-mono">// ESTADO DE CUENTA DEL TRABAJADOR</div>
            <h2 className="font-display font-bold text-lg text-slate-100 mt-1">Préstamos, cargos y abonos</h2>
          </div>
          <button type="button" onClick={onClose} className="btn-ghost p-1.5 rounded-sm"><X className="w-4 h-4" /></button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div>
            <label className="label-mono block mb-1.5">Trabajador *</label>
            {emps.length === 0 ? (
              <p className="text-xs text-slate-500 px-3 py-2 rounded-sm" style={{ border: "1px dashed var(--border)" }} data-testid="statement-empty">
                No hay trabajadores con préstamos o cargos registrados.
              </p>
            ) : (
              <select data-testid="statement-employee" value={empId} onChange={(e) => setEmpId(e.target.value)} className="input-cyber px-3 py-2 text-sm w-full">
                {emps.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
              </select>
            )}
          </div>
          {selected && (
            <div className="rounded-sm p-3 flex items-center justify-between text-xs" style={{ background: "rgba(251,191,36,0.07)", border: "1px solid rgba(251,191,36,0.3)" }} data-testid="statement-summary">
              <span className="text-slate-300">{selected.count} registro{selected.count === 1 ? "" : "s"} en su historial</span>
              <span className="font-mono" style={{ color: "#fbbf24" }}>Saldo: <b>{fmtMXN(selected.saldo)}</b></span>
            </div>
          )}
          <p className="text-[11px] text-slate-500">💡 Incluye el historial completo: préstamos, adelantos, cargos por compra y todos sus abonos, con saldo al día. El trabajador firma en pantalla y la firma queda plasmada en el PDF.</p>
        </div>
        <div className="px-6 py-4 border-t flex justify-end gap-2" style={{ borderColor: "var(--border)" }}>
          <button type="button" onClick={onClose} className="btn-ghost px-4 py-2 rounded-sm text-xs uppercase tracking-widest">Cancelar</button>
          <button type="button" disabled={!empId || downloading} onClick={() => setShowSign(true)} data-testid="statement-sign-btn"
            className="btn-primary px-4 py-2 rounded-sm text-xs uppercase tracking-widest flex items-center gap-2 disabled:opacity-40">
            {downloading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />} Firmar y descargar PDF
          </button>
        </div>
      </div>
      {showSign && (
        <SignaturePad
          title={`Estado de cuenta · ${selected?.name || ""}`}
          saving={downloading}
          onConfirm={download}
          onClose={() => setShowSign(false)}
        />
      )}
    </div>
  );
}

function NewLoanModal({ employees, onClose, onDone }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    employee_id: "", type: "prestamo", amount: "", reason: "", loan_date: today,
    concept: "", deduction_mode: "integro", installment_amount: "",
    create_expense: true, expense_category: "otros", payment_method: "efectivo",
  });
  const [ticketFile, setTicketFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const isCargo = form.type === "cargo";
  const isParcial = form.deduction_mode === "parcialidades";

  const save = async (e) => {
    e.preventDefault();
    if (isCargo && !form.concept.trim()) { toast.error("Escribe el concepto de la compra"); return; }
    if (isParcial && !(Number(form.installment_amount) > 0)) { toast.error("Indica el monto de la parcialidad"); return; }
    setSaving(true);
    try {
      let receipt_path = null;
      if (isCargo && ticketFile) {
        const fd = new FormData();
        fd.append("file", ticketFile);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        receipt_path = data.id;
      }
      await api.post("/employee-loans", {
        employee_id: form.employee_id,
        type: form.type,
        amount: Number(form.amount),
        reason: form.reason || null,
        loan_date: form.loan_date,
        deduction_mode: form.deduction_mode,
        installment_amount: isParcial ? Number(form.installment_amount) : null,
        ...(isCargo ? {
          concept: form.concept.trim(),
          receipt_path,
          create_expense: form.create_expense,
          expense_category: form.expense_category,
          payment_method: form.payment_method,
        } : {}),
      });
      toast.success(isParcial
        ? `Registrado — se descontarán ${fmtMXN(Number(form.installment_amount))} por raya hasta liquidar`
        : "Registrado — se descontará en su próximo pago de nómina");
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al registrar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(5,8,18,0.85)", backdropFilter: "blur(6px)" }} onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="w-full max-w-md panel-strong flex flex-col" style={{ maxHeight: "92vh" }} data-testid="new-loan-modal">
        <div className="px-6 py-4 border-b flex items-center justify-between shrink-0" style={{ borderColor: "var(--border)" }}>
          <div>
            <div className="label-mono">// PRÉSTAMO · ADELANTO · CARGO POR COMPRA</div>
            <h2 className="font-display font-bold text-lg text-slate-100 mt-1">Nuevo registro</h2>
          </div>
          <button type="button" onClick={onClose} className="btn-ghost p-1.5 rounded-sm"><X className="w-4 h-4" /></button>
        </div>
        <div className="px-6 py-5 space-y-3 overflow-y-auto">
          <div>
            <label className="label-mono block mb-1.5">Trabajador *</label>
            <select required data-testid="loan-employee" value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value })} className="input-cyber px-3 py-2 text-sm w-full">
              <option value="">— Selecciona —</option>
              {employees.filter((e) => e.active !== false).map((e) => (
                <option key={e.id} value={e.id}>{e.name} {e.payroll_type === "obra" ? "(OBRA)" : ""}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-mono block mb-1.5">Tipo *</label>
              <select data-testid="loan-type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="input-cyber px-3 py-2 text-sm w-full">
                <option value="prestamo">Préstamo</option>
                <option value="adelanto">Adelanto de raya</option>
                <option value="cargo">Cargo por compra</option>
              </select>
            </div>
            <div>
              <label className="label-mono block mb-1.5">Monto MXN *</label>
              <input required type="number" min="1" step="0.01" data-testid="loan-amount" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="input-cyber px-3 py-2 text-sm w-full" />
            </div>
          </div>
          {isCargo && (
            <div>
              <label className="label-mono block mb-1.5">¿Qué se le compró? *</label>
              <input required data-testid="cargo-concept" value={form.concept} onChange={(e) => setForm({ ...form, concept: e.target.value })} placeholder="Ej. llanta, estéreo, gasolina, herramienta…" className="input-cyber px-3 py-2 text-sm w-full" />
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-mono block mb-1.5">Fecha *</label>
              <input required type="date" data-testid="loan-date" value={form.loan_date} onChange={(e) => setForm({ ...form, loan_date: e.target.value })} className="input-cyber px-3 py-2 text-sm w-full" />
            </div>
            <div>
              <label className="label-mono block mb-1.5">Descuento en nómina</label>
              <select data-testid="loan-deduction-mode" value={form.deduction_mode} onChange={(e) => setForm({ ...form, deduction_mode: e.target.value })} className="input-cyber px-3 py-2 text-sm w-full">
                <option value="integro">Todo en la siguiente raya</option>
                <option value="parcialidades">En parcialidades</option>
              </select>
            </div>
          </div>
          {isParcial && (
            <div>
              <label className="label-mono block mb-1.5">Parcialidad por raya (MXN) *</label>
              <input required type="number" min="1" step="0.01" data-testid="loan-installment" value={form.installment_amount} onChange={(e) => setForm({ ...form, installment_amount: e.target.value })} placeholder="Ej. 300" className="input-cyber px-3 py-2 text-sm w-full" />
              {Number(form.amount) > 0 && Number(form.installment_amount) > 0 && (
                <p className="text-[11px] text-slate-500 mt-1">≈ {Math.ceil(Number(form.amount) / Number(form.installment_amount))} pagos de {fmtMXN(Number(form.installment_amount))}</p>
              )}
            </div>
          )}
          {isCargo && (
            <>
              <div>
                <label className="label-mono block mb-1.5">Ticket / foto de la compra (opcional)</label>
                <input type="file" accept="image/*,application/pdf" data-testid="cargo-ticket"
                  onChange={(e) => setTicketFile(e.target.files?.[0] || null)}
                  className="w-full text-xs text-slate-400 file:btn-ghost file:px-3 file:py-1.5 file:rounded-sm file:border file:text-xs file:mr-3" />
                {ticketFile && <p className="text-[11px] mt-1" style={{ color: "var(--cyan)" }}>📎 {ticketFile.name}</p>}
              </div>
              <div className="rounded-sm p-3 space-y-3" style={{ background: "rgba(34,211,238,0.05)", border: "1px solid rgba(34,211,238,0.2)" }}>
                <label className="flex items-center gap-2 cursor-pointer text-xs text-slate-200">
                  <input type="checkbox" checked={form.create_expense} data-testid="cargo-create-expense"
                    onChange={(e) => setForm({ ...form, create_expense: e.target.checked })} className="accent-cyan-400" />
                  <span>Registrar también como <b>gasto de la empresa</b> (la caja y contabilidad cuadran)</span>
                </label>
                {form.create_expense && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="label-mono block mb-1.5">Categoría del gasto</label>
                      <select data-testid="cargo-expense-category" value={form.expense_category} onChange={(e) => setForm({ ...form, expense_category: e.target.value })} className="input-cyber px-3 py-2 text-sm w-full">
                        {EXPENSE_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="label-mono block mb-1.5">Forma de pago</label>
                      <select data-testid="cargo-payment-method" value={form.payment_method} onChange={(e) => setForm({ ...form, payment_method: e.target.value })} className="input-cyber px-3 py-2 text-sm w-full">
                        {EXPENSE_PAYMENT_METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                      </select>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
          <div>
            <label className="label-mono block mb-1.5">{isCargo ? "Notas (opcional)" : "Motivo (opcional)"}</label>
            <input data-testid="loan-reason" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder={isCargo ? "Ej. para su camioneta, autorizado por…" : "Ej. emergencia médica, adelanto de semana…"} className="input-cyber px-3 py-2 text-sm w-full" />
          </div>
          <p className="text-[11px] text-slate-500">💡 {isParcial
            ? "En cada raya se prellenará la parcialidad como deducción hasta liquidar el saldo (podrás ajustarla antes de confirmar)."
            : "El saldo completo se descontará automáticamente en su próximo pago de nómina (podrás ajustarlo antes de confirmar)."}</p>
        </div>
        <div className="px-6 py-4 border-t flex justify-end gap-2 shrink-0" style={{ borderColor: "var(--border)" }}>
          <button type="button" onClick={onClose} className="btn-ghost px-4 py-2 rounded-sm text-xs uppercase tracking-widest">Cancelar</button>
          <button type="submit" disabled={saving} data-testid="loan-submit" className="btn-primary px-4 py-2 rounded-sm text-xs uppercase tracking-widest flex items-center gap-2">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : isCargo ? <ShoppingCart className="w-3.5 h-3.5" /> : <HandCoins className="w-3.5 h-3.5" />} Registrar
          </button>
        </div>
      </form>
    </div>
  );
}

function AbonoModal({ loan, onClose, onDone }) {
  const [form, setForm] = useState({ amount: loan.balance, date: new Date().toISOString().slice(0, 10), note: "" });
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/employee-loans/${loan.id}/payment`, { ...form, amount: Number(form.amount) });
      toast.success("Abono registrado");
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al abonar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(5,8,18,0.85)", backdropFilter: "blur(6px)" }} onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="w-full max-w-sm panel-strong" data-testid="abono-modal">
        <div className="px-6 py-4 border-b" style={{ borderColor: "var(--border)" }}>
          <div className="label-mono">// ABONO MANUAL</div>
          <h2 className="font-display font-bold text-base text-slate-100 mt-1">{loan.employee_name} · saldo {fmtMXN(loan.balance)}</h2>
        </div>
        <div className="px-6 py-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-mono block mb-1.5">Monto *</label>
              <input required type="number" min="0.01" max={loan.balance} step="0.01" data-testid="abono-amount" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="input-cyber px-3 py-2 text-sm w-full" />
            </div>
            <div>
              <label className="label-mono block mb-1.5">Fecha *</label>
              <input required type="date" data-testid="abono-date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="input-cyber px-3 py-2 text-sm w-full" />
            </div>
          </div>
          <div>
            <label className="label-mono block mb-1.5">Nota</label>
            <input data-testid="abono-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Ej. pagó en efectivo" className="input-cyber px-3 py-2 text-sm w-full" />
          </div>
        </div>
        <div className="px-6 py-4 border-t flex justify-end gap-2" style={{ borderColor: "var(--border)" }}>
          <button type="button" onClick={onClose} className="btn-ghost px-4 py-2 rounded-sm text-xs uppercase tracking-widest">Cancelar</button>
          <button type="submit" disabled={saving} data-testid="abono-submit" className="btn-primary px-4 py-2 rounded-sm text-xs uppercase tracking-widest flex items-center gap-2">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wallet className="w-3.5 h-3.5" />} Abonar
          </button>
        </div>
      </form>
    </div>
  );
}
